import { useState } from "react";
import { Dialog } from '../dialogs/dialog';
import { ExplainedButton as Button } from '../explained-button';
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { MultiSelect } from 'primereact/multiselect';
import { isMultiChoiceType, selectionKeys, expectsBlankInline } from '../content-viewer/multi-choice-fields';

import { FormFieldAttributesDto, FormFieldDto, FormFieldType, FormFieldOption } from "../../general/dtos";
import { ManageAttributesDialog } from "./field-attributes-dialog";
import './add-edit-field-dialog.css';

interface AddEditFieldDialogProps {
  mode: "add" | "edit";
  field?: FormFieldDto;
  onSave: (field: FormFieldDto) => void;
  onCancel: () => void;
}

export function AddEditFieldDialog({
  mode,
  field,
  onSave,
  onCancel
}: AddEditFieldDialogProps) {
  const [type, setType] = useState<FormFieldType>(field?.type ?? "text");
  const [label, setLabel] = useState<string>(field?.label ?? "");
  const [isLabelHidden, setIsLabelHidden] = useState(Boolean(field?.is_label_hidden ?? false));
  const [required, setRequired] = useState<boolean>(Boolean(field?.required ?? false));
  const [placeholder, setPlaceholder] = useState<string>(field?.placeholder ?? "");
  const [maxLength, setMaxLength] = useState<number | undefined>(field?.maxLength ?? undefined);

  // Strict FormFieldOption[] state
  const [options, setOptions] = useState<FormFieldOption[]>(field?.options ?? []);

  const [defaultValue, setDefaultValue] = useState<string>(Array.isArray(field?.defaultValue) ? '' : field?.defaultValue ?? "");
  const [expectedValue, setExpectedValue] = useState<string>(Array.isArray(field?.expectedValue) ? '' : field?.expectedValue ?? "");
  const [defaultSelections, setDefaultSelections] = useState<string[]>(selectionKeys(field?.defaultValue));
  const [expectedSelections, setExpectedSelections] = useState<string[]>(selectionKeys(field?.expectedValue));
  const [checkExpectedSelections, setCheckExpectedSelections] = useState(Array.isArray(field?.expectedValue));
  const isMultiChoice = isMultiChoiceType(type);
  const isInlineText = type === 'inline-text';
  const [checkInlineExpected, setCheckInlineExpected] = useState(field ? field.expectedValue != null : true);
  const expectsBlank = isInlineText && checkInlineExpected && expectsBlankInline(type, expectedValue);
  const [tooltip, setTooltip] = useState<string>(field?.tooltip ?? "");
  const [aiHint, setAiHint] = useState(field?.aiHint ?? '');
  const [weight, setWeight] = useState<string>(String(field?.weight ?? 1));
  const weightValue = weight.trim() === '' ? 1 : Number(weight);
  const weightValid = Number.isInteger(weightValue) && weightValue >= 0 && weightValue <= 2147483647;
  const [attrs, setAttrs] = useState<FormFieldAttributesDto>(field?.attributes ?? {});
  const [showAdvanced, setShowAdvanced] = useState(false);

  const typeOptions = [
    { label: "Text", value: "text" },
    { label: "Inline text", value: "inline-text" },
    { label: "Textarea", value: "textarea" },
    { label: "Number", value: "number" },
    { label: "Email", value: "email" },
    { label: "Select", value: "select" },
    { label: "Radio group", value: "radio" },
    { label: "Checkbox", value: "checkbox" },
    { label: 'Checkbox group', value: 'checkbox-group' },
    { label: 'Multi-choice select', value: 'multi-select' },
    { label: "Date", value: "date" },
    { label: "Quiz options", value: "quiz-list" }
  ];

  const handleSave = () => {
    if (!weightValid) return;
    const newField: FormFieldDto = {
      key: field?.key ?? crypto.randomUUID(),
      type,
      label,
      is_label_hidden: isLabelHidden,
      required: expectsBlank ? false : required,
      placeholder,
      maxLength,
      options,
      defaultValue: isMultiChoice ? defaultSelections.filter(key => options.some(option => option.key === key)) : defaultValue,
      expectedValue: isMultiChoice ? (checkExpectedSelections
        ? expectedSelections.filter(key => options.some(option => option.key === key)) : null)
        : isInlineText && !checkInlineExpected ? null : expectedValue,
      tooltip,
      aiHint: aiHint.trim() || null,
      weight: weightValue || 1,
      attributes: attrs,
    };

    onSave(newField);
  };

  const addOption = () => setOptions([...options, { key: `opt_${crypto.randomUUID().split('-')[0]}`, value: "" }]);

  const updateOption = (i: number, value: string) => {
    const next = [...options];
    next[i] = { ...next[i], value };
    setOptions(next);
  };

  const removeOption = (i: number) => {
    const next = [...options];
    next.splice(i, 1);
    setDefaultSelections(previous => previous.filter(key => key !== options[i].key));
    setExpectedSelections(previous => previous.filter(key => key !== options[i].key));
    setOptions(next);
  };

  const moveOption = (index: number, direction: 'up' | 'down') => {
    const next = [...options];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= next.length) return;
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    setOptions(next);
  };

  // Logic to determine if we show the options editor
  const hasOptions = type === "select" || type === "radio" || type === "quiz-list" || isMultiChoice;
  // For quiz-list, we hide default/expected values as they are set per-target in HTML
  const showStandardValues = type !== "quiz-list";

  const footer = (
    <div className="flex justify-content-between align-items-center w-full">
        <Button type="button" label="Field attributes..." icon="pi pi-sliders-h" className="p-button-outlined p-button-secondary p-button-sm"
        onClick={() => setShowAdvanced(true)} tooltip="Customize container, label, and input styles" />

        <div className="flex gap-2">
        <Button type="button" label="Cancel" className="p-button-text p-button-secondary" onClick={onCancel}  />
        <Button type="button" label={mode === "add" ? "Create field" : "Update field"} icon="pi pi-check" onClick={handleSave}
            disabled={
                !weightValid ||
                (!isInlineText && !label.trim()) ||
                (hasOptions && options.length === 0)
            }
            disabledReason={!weightValid ? 'Enter a whole number from 0 to 2147483647 for the question weight.'
                : !isInlineText && !label.trim() ? 'Enter a field label first.' : 'Add at least one option first.'}
        />
        </div>
    </div>
  );

  return (<>
    <Dialog
      header={mode === "add" ? "Add field" : "Edit field"}
      visible
      modal
      className="field-properties-dialog"
      style={{ width: "600px", maxWidth: "95vw" }}
      onHide={onCancel}
      footer={footer}
    >
      <div className="flex flex-column gap-3">
        {mode === "add" && (
          <div className="flex flex-column gap-1">
            <label htmlFor="field-type" className="font-semibold">Field type</label>
            <Dropdown inputId="field-type" value={type} options={typeOptions} onChange={(e) => {
              setType(e.value);
              if (e.value === 'inline-text') setRequired(true);
            }} scrollHeight={'500px'}/>
          </div>
        )}

        <div className="field-property-row">
          <label htmlFor="field-label" className="font-semibold">{isInlineText ? 'Label for results (optional)' : 'Label'}</label>
          <InputText id="field-label" value={label} maxLength={1000} onChange={(e) => setLabel(e.target.value)}
            aria-describedby={isInlineText ? 'field-label-description' : undefined} />
          {isInlineText && <small id="field-label-description">Optional name to identify this blank in results.</small>}
          <small>Markdown supported: **bold**, *italic*, ==highlight==, H~2~O, x^2^ and :joy:.</small>
        </div>

        <div className="flex align-items-center gap-2">
          <Checkbox inputId="field-label-hidden" checked={isLabelHidden} onChange={event => setIsLabelHidden(event.checked ?? false)} />
          <label htmlFor="field-label-hidden">Hide label</label>
          &nbsp;&nbsp;
          <Checkbox inputId="field-required" checked={expectsBlank ? false : required} disabled={expectsBlank} onChange={(e) => { setRequired(e.checked ?? false); }} />
          <label htmlFor="field-required">Required</label>
        </div>

        <div className="field-property-row">
          <label htmlFor="field-weight" className="font-semibold">Question weight</label>
          <InputText id="field-weight" type="number" min="0" max="2147483647" step="1" value={weight} aria-invalid={!weightValid}
            aria-describedby="field-weight-description" onChange={event => setWeight(event.target.value)} />
          <small id="field-weight-description">Maximum points for a correct answer (whole number). Blank or zero means 1.</small>
          {!weightValid && <small role="alert" className="p-error">Enter a whole number from 0 to 2147483647 for the question weight.</small>}
        </div>

        {['text','inline-text','textarea'].includes(type) && <div className="flex align-items-center gap-2">
          <Checkbox inputId="field-word-suggestions" checked={!attrs.input?.some(record => record['data-word-completion'] === 'off')}
            onChange={event => setAttrs(current => ({ ...current, input: [
              ...(current.input ?? []).map(record => Object.fromEntries(Object.entries(record).filter(([key]) => key.toLowerCase() !== 'data-word-completion'))).filter(record => Object.keys(record).length),
              ...(!event.checked ? [{ 'data-word-completion': 'off' }] : []),
            ] }))} />
          <label htmlFor="field-word-suggestions" title="Learners can enable English word suggestions. Turn this off for spelling exercises or other answers where suggestions should not be offered.">Allow word suggestions</label>
        </div>}

        <div className="field-property-row">
          <label htmlFor="field-placeholder" className="font-semibold">Placeholder</label>
          <InputText id="field-placeholder" value={placeholder} onChange={(e) => setPlaceholder(e.target.value)} />
        </div>

        <div className="field-property-row">
          <label htmlFor="field-tooltip" className="font-semibold">Tooltip</label>
          <InputTextarea id="field-tooltip" rows={3} value={tooltip} onChange={(e) => setTooltip(e.target.value)} />
          <small>Markdown supported, including paragraphs and lists.</small>
        </div>

        <div className="flex flex-column gap-1">
          <label htmlFor="field-ai-hint" className="font-semibold">AI hint (optional)</label>
          <InputTextarea id="field-ai-hint" value={aiHint} onChange={event => setAiHint(event.target.value)} rows={3} autoResize
            aria-describedby="field-ai-hint-description" />
          <small id="field-ai-hint-description">Guide AI answer verification for this field, such as acceptable alternatives or points to check. This is separate from the learner tooltip.</small>
        </div>

        {(type === "text" || type === "textarea" || isInlineText) && (
          <div className="field-property-row">
            <label htmlFor="field-max-length" className="font-semibold">Max length</label>
            <InputText id="field-max-length" type="number" value={(maxLength ?? 0).toString()} onChange={(e) => setMaxLength(Number(e.target.value))} />
          </div>
        )}

        {hasOptions && (
          <div className="flex flex-column gap-2">
            <div className="flex justify-content-between align-items-center">
                <label className="font-semibold">{type === 'quiz-list' ? 'Quiz answer pool' : 'Options'}</label>
                {type === 'quiz-list' && <span className="text-xs text-400">Keys are used for document references</span>}
            </div>

            {options.map((opt, i) => (
              <div key={opt.key} className="flex gap-2 align-items-center">
                <InputText
                    value={opt.value}
                    onChange={(e) => updateOption(i, e.target.value)}
                    className="flex-1"
                    placeholder="Label"
                />

                {/* Always show key prefix for auditability in the new structure */}
                <span className="text-xs text-500 font-mono bg-100 p-1 border-round" title={opt.key}>
                    {(opt.key ?? '').replace('opt_', '')}
                </span>

                <div className="flex">
                    <Button icon="pi pi-angle-up" aria-label="Move option up" className="p-button-text p-button-sm p-0 w-2rem" disabled={i === 0} disabledReason="This is already the first option." onClick={() => moveOption(i, 'up')} />
                    <Button icon="pi pi-angle-down" aria-label="Move option down" className="p-button-text p-button-sm p-0 w-2rem" disabled={i === options.length - 1} disabledReason="This is already the last option." onClick={() => moveOption(i, 'down')} />
                </div>
                <Button icon="pi pi-trash" className="p-button-text p-button-sm p-button-danger" onClick={() => removeOption(i)} />
              </div>
            ))}

            <Button label="Add option" icon="pi pi-plus" className="p-button-text p-button-sm" onClick={addOption} />
          </div>
        )}

        {isMultiChoice && <>
          <div className="field-property-row">
            <label htmlFor="field-default-selections" className="font-semibold">Default values</label>
            <MultiSelect inputId="field-default-selections" value={defaultSelections} options={options}
              optionLabel="value" optionValue="key" display="chip" placeholder="No default selections"
              onChange={event => setDefaultSelections(event.value)} />
          </div>
          <div className="flex align-items-center gap-2">
            <Checkbox inputId="field-check-expected" checked={checkExpectedSelections}
              onChange={event => setCheckExpectedSelections(event.checked ?? false)} />
            <label htmlFor="field-check-expected">Check expected selections</label>
          </div>
          {checkExpectedSelections && <div className="field-property-row">
            <label htmlFor="field-expected-selections" className="font-semibold">Expected values</label>
            <MultiSelect inputId="field-expected-selections" value={expectedSelections} options={options}
              optionLabel="value" optionValue="key" display="chip" placeholder="Expect no selections"
              onChange={event => setExpectedSelections(event.value)} />
            <small>Select every expected option. Selection order does not matter.</small>
          </div>}
        </>}
        {showStandardValues && !isMultiChoice && (
            <>
                <div className="field-property-row">
                    <label htmlFor="field-default-value" className="font-semibold">Default value</label>
                    <InputText id="field-default-value" value={defaultValue} onChange={(e) => setDefaultValue(e.target.value)} />
                </div>

                {isInlineText && <div className="flex align-items-center gap-2">
                    <Checkbox inputId="field-check-inline-expected" checked={checkInlineExpected}
                        onChange={event => setCheckInlineExpected(event.checked ?? false)} />
                    <label htmlFor="field-check-inline-expected">Check expected answer</label>
                </div>}
                {(!isInlineText || checkInlineExpected) && <div className="field-property-row">
                    <label htmlFor="field-expected-value" className="font-semibold">Expected value</label>
                    <InputText id="field-expected-value" value={expectedValue} onChange={(e) => setExpectedValue(e.target.value)} placeholder="Expected value" />
                    {isInlineText && <small>An empty expected value means the correct answer is to leave the blank empty (for example, no article). Uncheck “Check expected answer” to leave this field unscored.</small>}
                    {expectsBlank && <small>Required is disabled because the correct answer is empty.</small>}
                </div>}
            </>
        )}
      </div>
    </Dialog>

    <ManageAttributesDialog
        visible={showAdvanced}
        initialAttributes={attrs}
        previewData={{ label, type, placeholder, options }}
        onSave={(updatedAttrs) => {
          setAttrs(updatedAttrs);
          setShowAdvanced(false);
        }}
        onCancel={() => setShowAdvanced(false)}
      />
  </>);
}
